import { Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { CandleStickItem, CandleStickRequest, CandleStickIntervals, CandleStickResolutions } from "../models/candle-stick";
import { Observable, throwError, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { PriceChartDuration, PriceDataPoint, PriceDataPointSeries, PriceDataSeries } from "../models/price-chart";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class CandleStickService {
  constructor(private api: ApiService) {}

  /**
   *
   * @param candlesRequest
   * @returns Observable<CandleStickItem[]>
   * Note: selected region timestamp will be rounded.
   */
  getCandles(candlesRequest: CandleStickRequest): Observable<CandleStickItem[]> {
    return this.api.get(
      `query Candles($endTime: Timestamp!, $intervalInSeconds: Int!, $pair: String!, $startTime: Timestamp!, $inCurrency: String) {
        candles(endTime: $endTime, intervalInSeconds: $intervalInSeconds, pair: $pair, startTime: $startTime, inCurrency: $inCurrency) {
          data {
            cc
            ch
            cl
            co
            cv
            c
            h
            l
            o
            v
            t
          }
          pair
        }
      }`,
      candlesRequest,
      false
    ).pipe(
      switchMap((response) => {
        if (response["errors"]) {
          return throwError(response);
        }

        return of(response['data']['candles']?.['data'] ?? []);
      }),
      this.api.unifyErrors()
    );
  }

  getPriceHistory(pair: string, duration: PriceChartDuration): Observable<PriceDataSeries> {
    const to = Math.floor(new Date().getTime() / 1000); // current time in seconds
    const from = Math.floor(to - CandleStickIntervals[duration]);
    const inCurrency = environment.currencies.globalCurrency;

    return this.getCandles({
      startTime: from.toString(),
      endTime: to.toString(),
      intervalInSeconds: CandleStickResolutions[duration],
      inCurrency,
      pair,
    }).pipe(
      map(response => {
        const [_, baseCurency] = pair?.split('-');
        const data = this.generateTimeValueSeries(response);

        const result: PriceDataSeries = {
          baseCurrencyData: {
            currency: baseCurency,
            data: data.baseCurrencyData
          },
          globalCurrencyData: {
            currency: inCurrency,
            data: data.globalCurrencyData
          },
          defaultCurrencyData: null
        };

        result.defaultCurrencyData = environment.currencies.globalCurrency ? result.globalCurrencyData : result.baseCurrencyData;

        return result;
      })
    );
  }

  // Get the close value from the candle stick for both base and rate currencies
  generateTimeValueSeries(data: CandleStickItem[]): PriceDataPointSeries {
    const baseCurrencyData: PriceDataPoint[] = [];
    const globalCurrencyData: PriceDataPoint[] = [];

    data.forEach(item => {
      const x = parseInt(item.t, 10) * 1000;
      baseCurrencyData.push({
        x,
        y: parseFloat(item.c)
      });

      globalCurrencyData.push({
        x,
        y: parseFloat(item.cc)
      });
    });

    return {
      baseCurrencyData,
      globalCurrencyData
    }
  }
}